import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FolderGit2, HardDrive, Sparkles, LayoutGrid, Image as ImageIcon, Film, Music, FileText } from "lucide-react";
import { toast } from "sonner";
import { apiGet, protectedGet, protectedDelete } from "@/lib/api";
import { fmtBytes } from "@/lib/files";
import { useWorkspace } from "@/context/WorkspaceContext";
import { UploadZone } from "@/components/files/UploadZone";
import { FileCard } from "@/components/files/FileCard";
import { GeneratedGallery } from "@/components/files/GeneratedGallery";
import { Lightbox } from "@/components/files/Lightbox";
import { cn } from "@/lib/utils";

const TABS = [
  { id: "library", label: "Library", icon: HardDrive },
  { id: "generated", label: "Generated", icon: Sparkles },
];
const KINDS = [
  { id: "", label: "All", icon: LayoutGrid },
  { id: "image", label: "Images", icon: ImageIcon },
  { id: "video", label: "Video", icon: Film },
  { id: "audio", label: "Audio", icon: Music },
  { id: "document", label: "Docs", icon: FileText },
];

export default function FilesPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "generated" ? "generated" : "library";
  const [kind, setKind] = useState("");
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(null);
  const { workspace } = useWorkspace();

  const load = useCallback(() => {
    protectedGet(`/files?workspace=${encodeURIComponent(workspace)}`).then(setFiles).catch(() => toast.error("Couldn't load files, Sir."));
  }, [workspace]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { apiGet("/storage/status").then(setStatus).catch(() => {}); }, []);

  const library = files.filter((f) => f.source !== "generated" && (!kind || f.kind === kind));
  const generated = files.filter((f) => f.source === "generated");
  const totalBytes = files.reduce((a, f) => a + (f.size || 0), 0);
  const storageOn = !!status?.ready;

  const remove = async (f) => {
    try {
      await protectedDelete(`/files/${f.id}`);
      setFiles((p) => p.filter((x) => x.id !== f.id));
      if (open?.id === f.id) setOpen(null);
      toast.message(`Removed “${f.prompt || f.name}”, Sir.`);
    } catch (e) {
      toast.error(e.message || "Delete failed.");
    }
  };
  const add = (rec) => setFiles((p) => [rec, ...p]);

  return (
    <div className="h-full overflow-y-auto p-5 lg:p-6" data-testid="files-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <FolderGit2 className="w-6 h-6 text-jarvis-cyan" />
          <div>
            <h1 className="font-mono text-xl lg:text-2xl uppercase tracking-[0.25em] font-bold text-jarvis-cyan jv-glow">Files</h1>
            <p className="font-mono text-[11px] text-slate-500 tracking-widest" data-testid="files-summary">
              {files.length} objects · {fmtBytes(totalBytes)} · {workspace.toUpperCase()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider" data-testid="storage-status">
          <span className={cn("w-2 h-2 rounded-full", storageOn ? "bg-jarvis-green shadow-[0_0_8px_#18c9ff]" : "bg-jarvis-amber shadow-[0_0_8px_#ffd34e]")} />
          <span className={storageOn ? "text-jarvis-green" : "text-jarvis-amber"}>Object storage {storageOn ? "online" : status ? "offline" : "…"}</span>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-4 border-b border-jarvis-cyan/15" role="tablist">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} data-testid={`files-tab-${t.id}`}
            onClick={() => setParams(t.id === "generated" ? { tab: "generated" } : {})}
            className={cn("relative flex items-center gap-2 px-4 py-2.5 font-brand text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors",
              tab === t.id ? "text-jarvis-cyanBright" : "text-slate-500 hover:text-slate-300")}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
            {t.id === "generated" && generated.length > 0 && <span className="font-num text-[10px] text-jarvis-violet">{generated.length}</span>}
            {tab === t.id && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-jarvis-cyan shadow-[0_0_10px_#18c9ff]" />}
          </button>
        ))}
      </div>

      {tab === "library" ? (
        <div className="space-y-4">
          <UploadZone workspace={workspace} onUploaded={add} disabled={!storageOn} />
          <div className="flex flex-wrap gap-1.5" data-testid="kind-filters">
            {KINDS.map((k) => (
              <button key={k.id} onClick={() => setKind(k.id)} data-testid={`kind-filter-${k.id || "all"}`}
                className={cn("flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider px-2.5 py-1.5 border transition-colors",
                  kind === k.id ? "border-jarvis-cyan/50 text-jarvis-cyanBright bg-jarvis-cyan/10" : "border-white/10 text-slate-500 hover:text-slate-300")}>
                <k.icon className="w-3 h-3" /> {k.label}
              </button>
            ))}
          </div>
          {library.length === 0 ? (
            <div className="jv-panel hud-chamfer p-10 text-center" data-testid="library-empty">
              <HardDrive className="w-8 h-8 text-slate-600 mx-auto mb-3" />
              <p className="font-ui text-sm text-slate-400">Nothing stored here yet, Sir.</p>
              <p className="font-ui text-[13px] text-slate-600 mt-1">Drop a file above to add it to this workspace.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3" data-testid="library-grid">
              {library.map((f) => <FileCard key={f.id} file={f} onDelete={remove} onOpen={setOpen} />)}
            </div>
          )}
        </div>
      ) : (
        <GeneratedGallery files={generated} workspace={workspace} enabled={!!status?.configured}
          onGenerated={add} onDelete={remove} onOpen={setOpen} />
      )}

      {open && <Lightbox file={open} onClose={() => setOpen(null)} onDelete={remove} />}
    </div>
  );
}
